import * as React from "react";

export type ShellTabKey = "collaborate" | "analysis" | "cenna" | "versions";

export type BottomTabItem = {
  id: ShellTabKey;
  label: string;
  /** Numeric count or short label (e.g. version). */
  badge?: number | string;
  /** Always reserve and show the badge slot — use 0 / — when empty. */
  alwaysShowBadge?: boolean;
  badgeAriaLabel?: string;
  /** Cenna mark + small AI badge (Ask Cenna tab). */
  showCennaMark?: boolean;
  /** When true, tab is visible but not selectable. */
  disabled?: boolean;
  /** Tooltip / title when disabled. */
  disabledTitle?: string;
};

function formatBadgeValue(badge: number | string | undefined): string {
  if (typeof badge === "number") {
    return badge > 99 ? "99+" : String(badge);
  }
  if (typeof badge === "string" && badge.trim()) {
    return badge;
  }
  return "0";
}

const CennaAiMark: React.FC = () => (
  <span className="plugin-bottom-tab__cenna-mark" aria-hidden>
    <img
      className="plugin-bottom-tab__cenna-mark-base"
      src="/assets/icon-128.png"
      alt=""
      width={16}
      height={16}
      draggable={false}
    />
  </span>
);

export type BottomTabsProps = {
  active: ShellTabKey;
  onChange: (tab: ShellTabKey) => void;
  tabs: BottomTabItem[];
};

export const BottomTabs: React.FC<BottomTabsProps> = ({ active, onChange, tabs }) => {
  return (
    <nav className="plugin-bottom-tabs" aria-label="Primary">
      {tabs.map((t) => {
        const isActive = active === t.id;
        const showBadge =
          !t.showCennaMark &&
          (t.alwaysShowBadge ||
            (typeof t.badge === "number" ? t.badge > 0 : Boolean(t.badge)));
        return (
          <button
            key={t.id}
            type="button"
            className={`plugin-bottom-tab${isActive ? " is-active" : ""}${t.disabled ? " is-disabled" : ""}`}
            onClick={() => {
              if (!t.disabled) onChange(t.id);
            }}
            aria-current={isActive ? "page" : undefined}
            aria-disabled={t.disabled || undefined}
            disabled={t.disabled}
            title={t.disabled ? t.disabledTitle : undefined}
          >
            <span className="plugin-bottom-tab__label">{t.label}</span>
            <span className="plugin-bottom-tab__badge-slot">
              {t.showCennaMark ? (
                <CennaAiMark />
              ) : showBadge ? (
                <span
                  className="plugin-bottom-tab__badge"
                  aria-label={t.badgeAriaLabel ?? formatBadgeValue(t.badge)}
                >
                  {formatBadgeValue(t.badge)}
                </span>
              ) : (
                <span className="plugin-bottom-tab__badge plugin-bottom-tab__badge--hidden" aria-hidden>
                  0
                </span>
              )}
            </span>
          </button>
        );
      })}
    </nav>
  );
};
